/**
 * ReferenceNode Component - Redesigned with "Photo Board" Aesthetic
 *
 * DESIGN GOALS:
 * - Adaptive sizing: Node dimensions match image aspect ratio (not fixed)
 * - Minimal chrome: No header bar, image-focused presentation
 * - Title overlay: Shows on hover with gradient background
 * - Photo board feel: Like physical photos on a board with varied shapes
 *
 * KEY DIFFERENCES FROM BEATNODE:
 * - Dimensions adapt to each image's aspect ratio (150-400px width, 100-500px height)
 * - No header container (image fills entire node)
 * - objectFit: cover (fills node, may crop) vs contain (shows full image)
 * - Thinner border (1px vs 2px, 3px when selected)
 * - Title overlay on hover instead of always-visible header
 */

import { memo, useCallback, useMemo, CSSProperties, useState, useRef } from 'react';
import { Handle, Position, NodeProps, NodeResizeControl } from '@xyflow/react';
import { ReferenceNodeData } from '@/types';
import { useProjectStore } from '@/stores/projectStore';
import { getImageUrl as getBrowserImageUrl } from '@/utils/browserAPI';
import { resizeImageForImport } from '@/utils/imageProcessing';
import { blobManager } from '@/utils/blobManager';
import ContextMenu, { ContextMenuItem } from '../ContextMenu/ContextMenu';

// ---------------------------------------------------------------------------
// ADAPTIVE SIZING ALGORITHM
// ---------------------------------------------------------------------------

/**
 * Calculate node dimensions based on image's natural aspect ratio
 * Maintains aspect ratio while staying within min/max bounds
 */
function calculateNodeDimensions(
  imageNaturalWidth: number,
  imageNaturalHeight: number
): { width: number; height: number } {
  const MIN_WIDTH = 150;
  const MAX_WIDTH = 400;
  const MIN_HEIGHT = 100;
  const MAX_HEIGHT = 500;
  const DEFAULT_WIDTH = 220; // Fallback if no image

  if (!imageNaturalWidth || !imageNaturalHeight) {
    return { width: DEFAULT_WIDTH, height: 160 }; // Default for no image
  }

  const aspectRatio = imageNaturalWidth / imageNaturalHeight;

  // Try fitting by width first (most common case)
  let width = DEFAULT_WIDTH;
  let height = width / aspectRatio;

  // If height exceeds max, fit by height instead
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    width = height * aspectRatio;
  }

  // If height is below min, increase to min
  if (height < MIN_HEIGHT) {
    height = MIN_HEIGHT;
    width = height * aspectRatio;
  }

  // Clamp width to bounds
  width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));

  // Recalculate height to maintain exact aspect ratio after width clamping
  height = width / aspectRatio;

  return { width: Math.round(width), height: Math.round(height) };
}

const ReferenceNode = memo(function ReferenceNode({ id, data, selected }: NodeProps) {
  // Cast data to our known type
  const nodeData = data as ReferenceNodeData;

  // Store actions
  const updateReferenceNode = useProjectStore((state) => state.updateReferenceNode);
  const selectReferenceNode = useProjectStore((state) => state.selectReferenceNode);
  const clearNoteSelection = useProjectStore((state) => state.clearNoteSelection);
  const deleteReferenceNode = useProjectStore((state) => state.deleteReferenceNode);
  const duplicateReferenceNode = useProjectStore((state) => state.duplicateReferenceNode);
  const sendReferenceAsBeatToEnd = useProjectStore((state) => state.sendReferenceAsBeatToEnd);
  const sendReferenceAsBeatAtPosition = useProjectStore((state) => state.sendReferenceAsBeatAtPosition);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const projectPath = useProjectStore((state) => state.projectPath);

  // Track image loading state and context menu
  const [imageError, setImageError] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showTitleOverlay, setShowTitleOverlay] = useState(false);

  // Resize state
  const [hasStartedResize, setHasStartedResize] = useState(false);
  const isResizingRef = useRef(false);
  const resizeStartDimensionsRef = useRef<{ width: number; height: number } | null>(null);

  // Ref for image element to measure natural dimensions
  const imageRef = useRef<HTMLImageElement>(null);

  // ---------------------------------------------------------------------------
  // IMAGE DIMENSION CALCULATION
  // ---------------------------------------------------------------------------

  /**
   * When image loads, calculate dimensions and store in node data
   * This ensures the node size adapts to match the image's aspect ratio
   */
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current) return;

    const img = imageRef.current;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    // Only update if dimensions changed or not yet stored
    if (
      nodeData.imageWidth !== naturalWidth ||
      nodeData.imageHeight !== naturalHeight ||
      !nodeData.nodeWidth ||
      !nodeData.nodeHeight
    ) {
      const { width, height } = calculateNodeDimensions(naturalWidth, naturalHeight);

      updateReferenceNode(id, {
        imageWidth: naturalWidth,
        imageHeight: naturalHeight,
        nodeWidth: width,
        nodeHeight: height,
      });
    }
  }, [id, updateReferenceNode, nodeData.imageWidth, nodeData.imageHeight, nodeData.nodeWidth, nodeData.nodeHeight]);

  // ---------------------------------------------------------------------------
  // DRAG AND DROP HANDLER
  // ---------------------------------------------------------------------------

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Get dropped files
      const files = Array.from(event.dataTransfer.files);
      const imageFile = files.find((f) =>
        f.type.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name)
      );

      if (!imageFile) return;

      try {
        // Resize image to optimize storage (2MP limit, same as beat images)
        const resizedBlob = await resizeImageForImport(imageFile);

        // Check if we have Electron API with file paths
        const filePath = (imageFile as File & { path?: string }).path;

        if (filePath && window.electronAPI && projectPath) {
          // Electron mode: Use resized blob as URL for copyImage
          const resizedUrl = URL.createObjectURL(resizedBlob);
          const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
          const result = await window.electronAPI.copyImage(resizedUrl, projectDir);
          URL.revokeObjectURL(resizedUrl);

          if (result.success && result.relativePath) {
            updateReferenceNode(id, { imagePath: result.relativePath });
          }
        } else {
          // Browser mode: create blob URL from resized image
          const key = `reference-image:${id}`;
          blobManager.create(resizedBlob, key);
          // Store the key (not the blob URL) so it can be looked up later
          updateReferenceNode(id, { imagePath: key });
        }

        // Update title if it's still the default
        if (nodeData.title === 'New Reference') {
          updateReferenceNode(id, {
            title: imageFile.name.replace(/\.[^/.]+$/, ''), // Remove file extension
          });
        }

        // Calculate dimensions from the resized blob
        const img = new Image();
        img.onload = () => {
          const { width, height } = calculateNodeDimensions(img.naturalWidth, img.naturalHeight);
          updateReferenceNode(id, {
            imageWidth: img.naturalWidth,
            imageHeight: img.naturalHeight,
            nodeWidth: width,
            nodeHeight: height,
          });
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(resizedBlob);
      } catch (error) {
        console.error('Failed to process image:', error);
      }
    },
    [id, updateReferenceNode, projectPath, nodeData.title]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleMouseDown = useCallback(() => {
    // Select this reference and clear note selection
    selectReferenceNode(id);
    clearNoteSelection();
  }, [id, selectReferenceNode, clearNoteSelection]);

  // ---------------------------------------------------------------------------
  // RESIZE HANDLERS WITH ASPECT RATIO LOCK
  // ---------------------------------------------------------------------------

  const handleResizeStart = useCallback(() => {
    if (!hasStartedResize) {
      saveHistory();
      setHasStartedResize(true);
    }
    isResizingRef.current = true;

    // Store starting dimensions for reference
    resizeStartDimensionsRef.current = {
      width: nodeData.userWidth || nodeData.nodeWidth || 220,
      height: nodeData.userHeight || nodeData.nodeHeight || 160,
    };
  }, [hasStartedResize, saveHistory, nodeData.userWidth, nodeData.userHeight, nodeData.nodeWidth, nodeData.nodeHeight]);

  // Live resize with aspect ratio lock - called during drag
  const handleResize = useCallback((_event: any, params: any) => {
    const imageWidth = nodeData.imageWidth || 220;
    const imageHeight = nodeData.imageHeight || 160;
    const aspectRatio = imageWidth / imageHeight;

    // For bottom-right resize, width is the primary dimension
    // Calculate height to maintain aspect ratio
    const newWidth = params.width;
    const newHeight = newWidth / aspectRatio;

    // Update store with aspect-locked dimensions
    updateReferenceNode(id, {
      userWidth: Math.round(newWidth),
      userHeight: Math.round(newHeight),
    });
  }, [id, nodeData.imageWidth, nodeData.imageHeight, updateReferenceNode]);

  const handleResizeEnd = useCallback(() => {
    setHasStartedResize(false);
    isResizingRef.current = false;
    resizeStartDimensionsRef.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // HOVER HANDLERS FOR TITLE OVERLAY
  // ---------------------------------------------------------------------------

  const handleMouseEnter = useCallback(() => {
    setShowTitleOverlay(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTitleOverlay(false);
  }, []);

  // ---------------------------------------------------------------------------
  // CONTEXT MENU
  // ---------------------------------------------------------------------------

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ x: event.clientX, y: event.clientY });
    selectReferenceNode(id);
  }, [id, selectReferenceNode]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleSendAsBeatToEnd = useCallback(() => {
    sendReferenceAsBeatToEnd(id);
  }, [id, sendReferenceAsBeatToEnd]);

  const handleSendAsBeatAtPosition = useCallback(() => {
    sendReferenceAsBeatAtPosition(id);
  }, [id, sendReferenceAsBeatAtPosition]);

  const handleDuplicate = useCallback(() => {
    saveHistory();
    // Get current node to find its position
    const nodes = useProjectStore.getState().referenceNodes;
    const currentNode = nodes.find(n => n.id === id);
    if (currentNode) {
      const offset = 50;
      duplicateReferenceNode(id, {
        x: currentNode.position.x + offset,
        y: currentNode.position.y + offset,
      });
    }
  }, [id, duplicateReferenceNode, saveHistory]);

  const handleDelete = useCallback(() => {
    saveHistory();
    deleteReferenceNode(id);
  }, [id, deleteReferenceNode, saveHistory]);

  const handleResetSize = useCallback(() => {
    saveHistory();
    // Clear user dimensions, recalculate adaptive size
    const { width, height } = calculateNodeDimensions(
      nodeData.imageWidth || 0,
      nodeData.imageHeight || 0
    );

    updateReferenceNode(id, {
      userWidth: undefined,
      userHeight: undefined,
      nodeWidth: width,
      nodeHeight: height,
    });
  }, [id, nodeData.imageWidth, nodeData.imageHeight, updateReferenceNode, saveHistory]);

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Send as Beat (Add to End)',
      onClick: handleSendAsBeatToEnd,
    },
    {
      label: 'Send as Beat (Add at Position)',
      onClick: handleSendAsBeatAtPosition,
    },
    {
      separator: true,
    },
    {
      label: 'Reset to Auto Size',
      onClick: handleResetSize,
      disabled: !nodeData.userWidth && !nodeData.userHeight, // Only enable if manually resized
    },
    {
      separator: true,
    },
    {
      label: 'Duplicate',
      onClick: handleDuplicate,
    },
    {
      label: 'Delete',
      onClick: handleDelete,
    },
  ];

  // ---------------------------------------------------------------------------
  // GET IMAGE URL
  // ---------------------------------------------------------------------------

  // Memoize image URL to prevent recalculation on every render
  const imageUrl = useMemo(() => {
    if (!nodeData.imagePath) return null;

    const imagePath = nodeData.imagePath;

    // Try browser API first (handles blob: URLs)
    const browserUrl = getBrowserImageUrl(imagePath);
    if (browserUrl) return browserUrl;

    // Fall back to Electron file:// URL
    if (!projectPath) return null;
    const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
    const absolutePath = `${projectDir}/${imagePath}`.replace(/\\/g, '/');
    return `file://${absolutePath}`;
  }, [nodeData.imagePath, projectPath]);

  // ---------------------------------------------------------------------------
  // TAG HASH COLOR (Consistent color per tag)
  // ---------------------------------------------------------------------------

  const getTagColor = (tag: string): string => {
    // Simple hash function to generate consistent colors
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Convert to HSL color (keep saturation and lightness consistent)
    const hue = hash % 360;
    return `hsl(${hue}, 60%, 45%)`;
  };

  // ---------------------------------------------------------------------------
  // STYLES - REDESIGNED FOR PHOTO BOARD AESTHETIC
  // ---------------------------------------------------------------------------

  // Use stored dimensions or defaults
  const nodeWidth = nodeData.nodeWidth || 220;
  const nodeHeight = nodeData.nodeHeight || 160;

  // Container style - image-only, no header
  // Don't set explicit width/height here - let React Flow handle it
  const nodeStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    border: selected
      ? '3px solid var(--nb-node-selected)'
      : nodeData.color
        ? `1px solid ${nodeData.color}`
        : '1px solid var(--nb-border-secondary)',
    borderRadius: '4px', // Subtle rounding for photo aesthetic
    overflow: 'visible', // Changed from 'hidden' to allow resize handles to show outside
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: selected
      ? '0 4px 12px rgba(0,0,0,0.15)' // Elevated shadow when selected
      : '0 2px 4px rgba(0,0,0,0.08)', // Subtle depth
    position: 'relative',
    backgroundColor: 'var(--nb-bg-primary)',
  };

  // Inner wrapper to clip image content while allowing resize handles outside
  const imageWrapperStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '4px',
    position: 'absolute',
    top: 0,
    left: 0,
  };

  // Image fills entire node with cover (crop to fill, no letterboxing)
  const imageStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // KEY CHANGE: cover instead of contain
    display: 'block',
  };

  // Title overlay - gradient background, shows on hover
  const titleOverlayStyles: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)',
    color: 'white',
    padding: '12px 8px 8px 8px', // Extra padding at top for gradient
    fontSize: '12px',
    fontWeight: 500,
    opacity: showTitleOverlay ? 1 : 0,
    transition: 'opacity 0.2s',
    pointerEvents: 'none', // Allow clicks to pass through
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // Placeholder for when no image is present
  const placeholderStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--nb-text-muted)',
    fontSize: 'var(--nb-font-size-xs)',
    textAlign: 'center',
    padding: 'var(--nb-space-sm)',
  };

  // Tags container - shown on hover below title
  const tagsOverlayStyles: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '4px 8px 32px 8px', // Extra bottom padding for title
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    opacity: showTitleOverlay && nodeData.tags && nodeData.tags.length > 0 ? 1 : 0,
    transition: 'opacity 0.2s',
    pointerEvents: 'none',
  };

  const tagPillStyles = (tag: string): CSSProperties => ({
    padding: '2px 6px',
    backgroundColor: getTagColor(tag),
    color: 'white',
    fontSize: '10px',
    borderRadius: 'var(--nb-radius-sm)',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  });

  // ---------------------------------------------------------------------------
  // RENDER - MINIMAL CHROME, IMAGE-FOCUSED
  // ---------------------------------------------------------------------------

  return (
    <>
      <div
        style={nodeStyles}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onMouseDownCapture={handleMouseDown}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Connection handles (hidden, for React Flow compatibility) */}
        <Handle
          type="target"
          position={Position.Left}
          style={{ opacity: 0 }}
        />

        {/* Inner wrapper to clip image content */}
        <div style={imageWrapperStyles}>
          {/* Image (fills entire node) or placeholder */}
          {imageUrl && !imageError ? (
            <>
              <img
                ref={imageRef}
                src={imageUrl}
                alt={nodeData.title}
                style={imageStyles}
                onError={() => setImageError(true)}
                onLoad={handleImageLoad}
                draggable={false}
              />

              {/* Title overlay (shows on hover) */}
              <div style={titleOverlayStyles}>
                {nodeData.title || 'Untitled Reference'}
              </div>

              {/* Tags overlay (shows on hover) */}
              {nodeData.tags && nodeData.tags.length > 0 && (
                <div style={tagsOverlayStyles}>
                  {nodeData.tags.slice(0, 3).map((tag) => (
                    <span key={tag} style={tagPillStyles(tag)}>
                      {tag}
                    </span>
                  ))}
                  {nodeData.tags.length > 3 && (
                    <span style={{
                      ...tagPillStyles(''),
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      color: 'white'
                    }}>
                      +{nodeData.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={placeholderStyles}>
              Drop image here
            </div>
          )}
        </div>

        {/* Resize handle - bottom-right only for stable resizing */}
        {selected && (
          <NodeResizeControl
            position="bottom-right"
            onResizeStart={handleResizeStart}
            onResize={handleResize}
            onResizeEnd={handleResizeEnd}
            style={{
              width: '14px',
              height: '14px',
              backgroundColor: 'var(--nb-primary)',
              border: '2px solid white',
              borderRadius: '50%',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          />
        )}

        <Handle
          type="source"
          position={Position.Right}
          style={{ opacity: 0 }}
        />
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems}
          onClose={handleCloseContextMenu}
        />
      )}
    </>
  );
});

export default ReferenceNode;
